// Reads a value until the API answers: a failing read is retried with backoff
// (1s, 2s, 4s, 8s, 15s, then every 30s), and at once on retryNow() (Try again,
// the tab coming back into view, the browser coming back online). Used for the
// two reads whose failure must NOT be taken as an answer: the onboarding status
// (unknown is neither "done" nor "not done") and whoami's tier (unknown is not
// lapsed). Persona QA 2026-10-25. The mobile app's lib/onboardingStatus.ts is
// the same loader.

export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000];
export const retryDelay = (failures) =>
  RETRY_DELAYS_MS[Math.min(Math.max(0, failures - 1), RETRY_DELAYS_MS.length - 1)];

// One silent retry before a "Reconnecting…" shows, so one blip doesn't flash it.
export const FAILURES_BEFORE_RECONNECTING = 2;

const defaultSchedule = (fn, ms) => {
  const t = setTimeout(fn, ms);
  return () => clearTimeout(t);
};

/**
 * `fetchValue` resolves to the answer (anything but undefined) or rejects.
 * onAnswer(value) once; onFailure({ failures, failed }) after each failed try.
 * `shouldRetry(error)` false (e.g. a 401: the session is gone) stops retrying.
 */
export function createRetryLoader({ fetchValue, onAnswer, onFailure = () => {}, shouldRetry = () => true, schedule = defaultSchedule }) {
  let failures = 0;
  let inFlight = false;
  let done = false;
  let stopped = false;
  let cancelTimer = null;

  const attempt = async () => {
    if (inFlight || done || stopped) return;
    cancelTimer?.();
    cancelTimer = null;
    inFlight = true;
    try {
      const value = await fetchValue();
      if (stopped) return;
      if (value === undefined) throw new Error("no answer");
      done = true;
      onAnswer(value);
    } catch (error) {
      if (stopped) return;
      failures += 1;
      onFailure({ failures, failed: failures >= FAILURES_BEFORE_RECONNECTING });
      if (shouldRetry(error)) cancelTimer = schedule(() => { attempt(); }, retryDelay(failures));
    } finally {
      inFlight = false;
    }
  };

  return {
    start: () => { attempt(); },
    retryNow: () => { attempt(); },
    stop: () => { stopped = true; cancelTimer?.(); cancelTimer = null; },
    get known() { return done; },
  };
}

/** Calls `cb` when the tab becomes visible again or the browser comes back online. */
export function onWakeOrOnline(cb) {
  const win = typeof window !== "undefined" ? window : null;
  const doc = typeof document !== "undefined" ? document : null;
  const vis = () => { if (doc?.visibilityState === "visible") cb(); };
  win?.addEventListener?.("online", cb);
  doc?.addEventListener?.("visibilitychange", vis);
  return () => {
    win?.removeEventListener?.("online", cb);
    doc?.removeEventListener?.("visibilitychange", vis);
  };
}

/**
 * Where Layout sends the athlete. Onboarding completion is tri-state (null =
 * unknown): unknown never opens the app shell, nor onboarding.
 */
export function layoutGate({ completed, failed }) {
  if (completed === true) return "app";
  if (completed === false) return "onboarding";
  return failed ? "reconnecting" : "loading";
}

export const STATUS_RECONNECTING = {
  title: "Reconnecting…",
  body: "We can’t reach Aithlo right now. Your training is safe; we’ll pick up where you left off.",
  retry: "Try again",
};
